import React, { useEffect, useState } from 'react';
import { get, post, del, downloadFile } from '../utils/api';
import { SUBTYPE_LABELS } from '../constants/subtypes';
import { useAuth } from '../App';

const fmt = n => new Intl.NumberFormat('ru-RU',{minimumFractionDigits:0}).format(Math.round(n||0)) + ' ₽';
const fmtPct = (a,b) => b>0 ? Math.round(a/b*100)+'%' : '—';
const MONTHS_RU = ['Янв','Фев','Мар','Апр','Май','Июн','Июл','Авг','Сен','Окт','Ноя','Дек'];
const monthLabel = key => { if (!key) return key; const [y,m] = key.split('-'); return `${MONTHS_RU[parseInt(m)-1]} ${y}`; };

export default function PLReportPage() {
  const { user } = useAuth();
  const canEditAccruals = ['admin','manager','fin_manager'].includes(user?.role);
  const now = new Date();
  const [from, setFrom] = useState(`${now.getFullYear()}-01-01`);
  const [to, setTo]   = useState(`${now.getFullYear()}-12-31`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accruals, setAccruals] = useState([]);
  const [showAccruals, setShowAccruals] = useState(false);
  const [accForm, setAccForm] = useState({ shareholder:'dominant', month:`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`, amount:'', comment:'' });
  const [accErr, setAccErr] = useState('');
  const [accSaving, setAccSaving] = useState(false);
  // Закрытие месяца
  const canCloseMonth = ['admin','fin_manager'].includes(user?.role);
  const isAdmin = user?.role === 'admin';
  const prevMonth = (() => { const d = new Date(now.getFullYear(), now.getMonth()-1, 1); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; })();
  const [closeMonth, setCloseMonth] = useState(prevMonth);
  const [closeStatus, setCloseStatus] = useState(null);
  const [closeBusy, setCloseBusy] = useState(false);
  const [closeErr, setCloseErr] = useState('');
  const [showClose, setShowClose] = useState(false);
  const loadCloseStatus = async (m) => {
    try { const st = await get(`/month-close/${m}/status`); setCloseStatus(st); } catch(e) { setCloseStatus(null); }
  };
  useEffect(()=>{ if (showClose) loadCloseStatus(closeMonth); },[closeMonth, showClose]);
  const doCloseMonth = async () => {
    setCloseErr('');
    if (!window.confirm(`Закрыть ${monthLabel(closeMonth)}? После закрытия проводки с периодом начисления в этом месяце будут невозможны. Открыть месяц сможет только администратор.`)) return;
    setCloseBusy(true);
    try { await post(`/month-close/${closeMonth}`, {}); await loadCloseStatus(closeMonth); await load(); }
    catch(e) { setCloseErr(e?.message || 'Ошибка'); } finally { setCloseBusy(false); }
  };
  const doReopenMonth = async () => {
    setCloseErr('');
    if (!window.confirm(`Открыть ${monthLabel(closeMonth)} заново? Проводки в этот месяц снова станут возможны.`)) return;
    setCloseBusy(true);
    try { await del(`/month-close/${closeMonth}`); await loadCloseStatus(closeMonth); await load(); }
    catch(e) { setCloseErr(e?.message || 'Ошибка'); } finally { setCloseBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const d = await get(`/reports/pl?from=${from}&to=${to}`); setData(d);
      const a = await get(`/dividend-accruals?from=${from}&to=${to}`); setAccruals(Array.isArray(a)?a:[]);
    }
    catch(e) {} finally { setLoading(false); }
  };
  useEffect(()=>{ load(); },[from,to]);

  const saveAccrual = async () => {
    setAccErr('');
    const amt = parseFloat(String(accForm.amount).replace(/\s/g,'').replace(',','.'));
    if (!isFinite(amt) || amt===0) return setAccErr('Введите сумму');
    if (!/^\d{4}-\d{2}$/.test(accForm.month)) return setAccErr('Месяц в формате ГГГГ-ММ');
    if (accSaving) return;
    setAccSaving(true);
    try {
      await post('/dividend-accruals', { ...accForm, amount: amt });
      setAccForm(f=>({ ...f, amount:'', comment:'' }));
      await load();
    } catch(e) { setAccErr(e?.message || 'Ошибка сохранения'); }
    finally { setAccSaving(false); }
  };
  const removeAccrual = async (id) => {
    if (!window.confirm('Удалить строку начисления?')) return;
    try { await del(`/dividend-accruals/${id}`); await load(); } catch(e) {}
  };

  if (!data && !loading) return null;

  const incomeByMonth  = Object.fromEntries((data?.income||[]).map(r=>[r.month,r.total]));
  const refundByMonth  = Object.fromEntries((data?.refundByMonth||[]).map(r=>[r.month,r.total]));
  const expenseByMonth = Object.fromEntries((data?.expenseByMonth||[]).map(r=>[r.month,r.total]));
  const totalRefund    = data?.totalRefund || 0;
  const netIncome      = (data?.totalIncome||0) - totalRefund;

  // category -> month -> total
  const catMonthMap = {};
  (data?.expByCatMonth||[]).forEach(r => {
    if (!catMonthMap[r.category]) catMonthMap[r.category] = {};
    catMonthMap[r.category][r.month] = r.total;
  });

  const months = [...new Set([
    ...(data?.income||[]).map(r=>r.month),
    ...(data?.refundByMonth||[]).map(r=>r.month),
    ...(data?.expenseByMonth||[]).map(r=>r.month),
  ])].sort();

  // Дивиденды акционеров: выплаты (транзакции) и начисления (справочник) по месяцам
  const SH = [
    { key:'dominant', sub:'dividend_dominant', label:'Доминант' },
    { key:'ak',       sub:'dividend_ak',       label:'АК' },
  ];
  const shares = { ak: data?.shares?.ak ?? 0.55, dominant: data?.shares?.dominant ?? 0.45 };
  const pct = v => Math.round(v*100)+'%';
  const paidMap = { ak:{}, dominant:{} };
  (data?.dividendsByMonth||[]).forEach(r => { const k = r.subtype==='dividend_ak'?'ak':'dominant'; paidMap[k][r.month] = (paidMap[k][r.month]||0) + r.total; });
  const accMap = { ak:{}, dominant:{} };      // начисления (распределение прибыли) — участвуют в пропорции
  const openMap = { ak:{}, dominant:{} };     // входящие остатки — история, в пропорции не участвуют
  (data?.accrualsByMonth||[]).forEach(r => {
    const map = r.kind === 'opening' ? openMap : accMap;
    if (map[r.shareholder]) map[r.shareholder][r.month] = (map[r.shareholder][r.month]||0) + r.total;
  });
  const opening = { ak: data?.dividendsOpening?.ak||0, dominant: data?.dividendsOpening?.dominant||0 };
  const divMonths = [...new Set([
    ...months,
    ...(data?.dividendsByMonth||[]).map(r=>r.month),
    ...(data?.accrualsByMonth||[]).map(r=>r.month),
  ])].sort();
  const sumOver = (map, k) => divMonths.reduce((s,m)=>s+(map[k][m]||0),0);
  const paidTotal = { ak: sumOver(paidMap,'ak'), dominant: sumOver(paidMap,'dominant') };
  const accTotal  = { ak: sumOver(accMap,'ak'),  dominant: sumOver(accMap,'dominant') };
  const paidAll = paidTotal.ak + paidTotal.dominant;
  // Остаток нарастающим: вх. остаток + начислено − выплачено, по месяцам
  const runningBalance = k => { let b = opening[k]; return divMonths.map(m => { b += (accMap[k][m]||0) + (openMap[k][m]||0) - (paidMap[k][m]||0); return b; }); };
  const openTotal = { ak: sumOver(openMap,'ak'), dominant: sumOver(openMap,'dominant') };
  const running = { ak: runningBalance('ak'), dominant: runningBalance('dominant') };
  const hasAccruals = (data?.accrualsByMonth||[]).length>0 || opening.ak!==0 || opening.dominant!==0;
  // Последний месяц с движением по дивидендам (начисление или выплата): дальше остатки и «прибыль минус
  // дивиденды» не показываем — месяц ещё не введён/не закрыт.
  const activeMonths = divMonths.filter(m => (accMap.ak[m]||0)!==0 || (accMap.dominant[m]||0)!==0 || (openMap.ak[m]||0)!==0 || (openMap.dominant[m]||0)!==0 || (paidMap.ak[m]||0)!==0 || (paidMap.dominant[m]||0)!==0);
  const lastActive = activeMonths.length ? activeMonths[activeMonths.length-1] : null;
  const isActive = m => lastActive !== null && m <= lastActive;
  const lastIdx = lastActive ? divMonths.indexOf(lastActive) : -1;
  // Пропорция: распределённая прибыль (начисления) должна делиться 55/45 между акционерами
  const paidMonthTotal = m => (paidMap.ak[m]||0)+(paidMap.dominant[m]||0);
  const accMonthTotal  = m => (accMap.ak[m]||0)+(accMap.dominant[m]||0);
  const accAll = accTotal.ak + accTotal.dominant;
  const fmtSigned = v => Math.abs(Math.round(v))<1 ? '0 ₽' : (v>0?'+':'−')+fmt(Math.abs(v));
  const deltaColor = v => Math.abs(Math.round(v))<1 ? 'var(--text3)' : (v>0?'var(--green)':'var(--red)');
  const balColor = v => Math.abs(Math.round(v))<1 ? 'var(--text3)' : (v>0?'var(--orange, #f59e0b)':'var(--red)');
  const showDividends = (data?.dividendsByMonth||[]).length > 0 || hasAccruals;
  const MONTH_OPTS = (() => { const out=[]; const d=new Date(now.getFullYear(), now.getMonth()+2, 1); for (let i=0;i<30;i++){ d.setMonth(d.getMonth()-1); out.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);} return out; })();

  const closedSet = new Set(data?.closedMonths||[]);
  // Закрытый месяц — компактный замочек, а не слово: иначе заголовок расширяет колонку и таблица «едет».
  const LockIcon = () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:'-1px',marginLeft:4,color:'var(--text3)'}} aria-label="Месяц закрыт">
      <rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>
    </svg>
  );
  const monthHead = m => closedSet.has(m)
    ? <span title="Месяц закрыт бухгалтером" style={{whiteSpace:'nowrap'}}>{monthLabel(m)}<LockIcon/></span>
    : monthLabel(m);
  const thStyle = { textAlign:'right', padding:'8px 10px', fontSize:12, whiteSpace:'nowrap', color:'var(--text2)' };
  const tdNum = (v, color) => ({ textAlign:'right', padding:'6px 10px', fontWeight:600, color: color || (v>=0?'var(--green)':'var(--red)'), whiteSpace:'nowrap' });

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Отчёт P&L</h1>
        <div style={{display:'flex',gap:8}}>
          {canCloseMonth && <button className={`btn ${showClose?'btn-secondary':'btn-primary'}`} onClick={()=>setShowClose(v=>!v)}>{showClose?'Скрыть закрытие месяца':'Закрытие месяца'}</button>}
          <button className="btn btn-secondary" onClick={()=>downloadFile('/export/excel','rental_pl.xlsx')}>⬇ Excel</button>
        </div>
      </div>

      {canCloseMonth && showClose && (
        <div className="card" style={{marginBottom:20}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:12,flexWrap:'wrap'}}>
            <div className="section-title" style={{marginBottom:0}}>Закрытие месяца</div>
            <div className="form-group" style={{marginBottom:0}}>
              <select value={closeMonth} onChange={e=>setCloseMonth(e.target.value)}>
                {MONTH_OPTS.filter(m=>m<=`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`).map(m=><option key={m} value={m}>{monthLabel(m)}{closedSet.has(m)?' · закрыт':''}</option>)}
              </select>
            </div>
          </div>
          <div style={{fontSize:12,color:'var(--text3)',margin:'6px 0 12px'}}>
            Бухгалтер закрывает месяц после ввода всех затрат, выплаты вознаграждения управляющему и дивидендов.
            После закрытия проводки с периодом начисления в этом месяце (транзакции, импорт, начисления дивидендов) невозможны. Открыть месяц заново может только администратор.
          </div>
          {!closeStatus && <p style={{color:'var(--text3)'}}>Загрузка...</p>}
          {closeStatus && (()=>{
            const st = closeStatus;
            const ok = (c) => c ? 'var(--green)' : 'var(--orange, #f59e0b)';
            const mgmtOk = Math.abs(st.mgmt.delta) < 1 && st.mgmt.count >= 1;
            const rows = [
              { label:'Доходы месяца (начисление)', value: fmt(st.income) },
              { label:'Расходы месяца', value: fmt(st.expense) },
              { label:'Прибыль до вознаграждения управляющего', value: fmt(st.profitBeforeMgmt) },
              { label:`Вознаграждение управляющего: проведено ${st.mgmt.count} транз.`, value: `${fmt(st.mgmt.paid)} из ${fmt(st.mgmt.expected)} (15%)`, color: ok(mgmtOk), note: mgmtOk ? 'сходится' : (st.mgmt.count===0 ? 'не проведено' : `расхождение ${st.mgmt.delta>0?'+':'−'}${fmt(Math.abs(st.mgmt.delta))}`) },
              { label:'Чистая прибыль месяца', value: fmt(st.profit), bold:true },
              { label:`Дивиденды АК выплачено (${Math.round(st.shares.ak*100)}%)`, value: fmt(st.dividends.ak), color: ok(st.dividends.akCount>0), note: st.dividends.akCount>0 ? `${st.dividends.akCount} транз.` : 'нет выплат' },
              { label:`Дивиденды Доминант выплачено (${Math.round(st.shares.dominant*100)}%)`, value: fmt(st.dividends.dominant), color: ok(st.dividends.dominantCount>0), note: st.dividends.dominantCount>0 ? `${st.dividends.dominantCount} транз.` : 'нет выплат' },
              { label:`Начислено АК (ожидается ${Math.round(st.shares.ak*100)}% от прибыли = ${fmt(st.profit*st.shares.ak)})`, value: fmt(st.accruals.ak), color: ok(Math.abs(st.accruals.ak - st.profit*st.shares.ak) < 1), note: Math.abs(st.accruals.ak - st.profit*st.shares.ak) < 1 ? 'сходится' : (st.accruals.akCount===0 ? 'не начислено' : `расхождение ${fmtSigned(st.accruals.ak - st.profit*st.shares.ak)}`) },
              { label:`Начислено Доминанту (ожидается ${Math.round(st.shares.dominant*100)}% от прибыли = ${fmt(st.profit*st.shares.dominant)})`, value: fmt(st.accruals.dominant), color: ok(Math.abs(st.accruals.dominant - st.profit*st.shares.dominant) < 1), note: Math.abs(st.accruals.dominant - st.profit*st.shares.dominant) < 1 ? 'сходится' : (st.accruals.dominantCount===0 ? 'не начислено' : `расхождение ${fmtSigned(st.accruals.dominant - st.profit*st.shares.dominant)}`) },
              { label:'Коммуналка/ФОТ без периода начисления (по дате)', value: String(st.warnings.utilNoPeriod), color: ok(st.warnings.utilNoPeriod===0), note: st.warnings.utilNoPeriod===0 ? 'нет' : 'проставить период' },
              { label:'Неразобранные операции импорта Т-Банка за месяц', value: String(st.warnings.pendingImports), color: ok(st.warnings.pendingImports===0), note: st.warnings.pendingImports===0 ? 'нет' : 'разобрать импорт' },
            ];
            return (<>
              <div className="table-wrap">
                <table>
                  <tbody>
                    {rows.map((r,i)=>(
                      <tr key={i}>
                        <td style={{fontSize:13,color:r.bold?'var(--text)':'var(--text2)',fontWeight:r.bold?700:400}}>{r.label}</td>
                        <td style={{textAlign:'right',fontWeight:600,whiteSpace:'nowrap',color:r.color||'var(--text)'}}>{r.value}</td>
                        <td style={{fontSize:12,color:r.color||'var(--text3)',whiteSpace:'nowrap'}}>{r.note||''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:12,marginTop:12,flexWrap:'wrap'}}>
                {st.closed ? (<>
                  <div style={{fontWeight:600,color:'var(--green)'}}>Месяц закрыт {st.closed.closed_at ? new Date(st.closed.closed_at.replace(' ','T')+'Z').toLocaleString('ru-RU') : ''}{st.closed.closed_by_name ? `, ${st.closed.closed_by_name}` : ''}</div>
                  {isAdmin && <button className="btn btn-ghost btn-sm" onClick={doReopenMonth} disabled={closeBusy}>Открыть месяц заново</button>}
                </>) : (
                  <button className="btn btn-primary" onClick={doCloseMonth} disabled={closeBusy}>Закрыть {monthLabel(closeMonth)}</button>
                )}
                {closeErr && <div style={{color:'var(--red)',fontSize:13}}>{closeErr}</div>}
              </div>
            </>);
          })()}
        </div>
      )}

      <div className="filters" style={{marginBottom:24}}>
        <div className="form-group"><label>С</label><input type="date" value={from} onChange={e=>setFrom(e.target.value)}/></div>
        <div className="form-group"><label>По</label><input type="date" value={to} onChange={e=>setTo(e.target.value)}/></div>
        <div style={{display:'flex',gap:8,alignItems:'flex-end'}}>
          {[
            {label:String(now.getFullYear()),        f:`${now.getFullYear()}-01-01`,   t:`${now.getFullYear()}-12-31`},
            {label:String(now.getFullYear()-1),      f:`${now.getFullYear()-1}-01-01`, t:`${now.getFullYear()-1}-12-31`},
          ].map((p,i)=>(
            <button key={i} className="btn btn-ghost btn-sm" onClick={()=>{ setFrom(p.f); setTo(p.t); }}>{p.label}</button>
          ))}
        </div>
      </div>

      {loading && <p style={{color:'var(--text3)'}}>Загрузка...</p>}

      {!loading && data && (<>
        {/* KPI */}
        <div className="stat-grid" style={{gridTemplateColumns:'repeat(4,1fr)',marginBottom:24}}>
          <div className="stat-card"><div className="stat-label">Доходы</div><div className="stat-value green">{fmt(data.totalIncome)}</div></div>
          <div className="stat-card"><div className="stat-label">Расходы</div><div className="stat-value red">{fmt(data.totalExpense)}</div></div>
          <div className="stat-card"><div className="stat-label">Чистая прибыль</div><div className={`stat-value ${data.profit>=0?'green':'red'}`}>{fmt(data.profit)}</div></div>
          <div className="stat-card"><div className="stat-label">Рентабельность</div><div className="stat-value blue">{fmtPct(data.profit,netIncome)}</div></div>
        </div>

        {/* Main P&L table */}
        <div className="card" style={{marginBottom:20}}>
          <div className="section-title">P&L по месяцам</div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{minWidth:200}}>Статья</th>
                  {months.map(m=><th key={m} style={thStyle}>{monthHead(m)}</th>)}
                  <th style={thStyle}>ИТОГО</th>
                </tr>
              </thead>
              <tbody>
                {/* Income row */}
                <tr style={{background:'rgba(34,197,94,0.07)'}}>
                  <td style={{fontWeight:700,color:'var(--green)'}}>Доходы</td>
                  {months.map(m=><td key={m} style={tdNum(incomeByMonth[m]||0,'var(--green)')}>{fmt(incomeByMonth[m]||0)}</td>)}
                  <td style={tdNum(data.totalIncome,'var(--green)')}>{fmt(data.totalIncome)}</td>
                </tr>

                {/* Возврат средств арендатору — контра-доход */}
                {totalRefund>0 && (
                  <tr>
                    <td style={{paddingLeft:24,fontSize:13,color:'var(--text2)'}}>↳ Возврат средств арендатору</td>
                    {months.map(m=>{
                      const v = refundByMonth[m]||0;
                      return <td key={m} style={{textAlign:'right',padding:'6px 10px',fontSize:13,color:v>0?'var(--red)':'var(--text3)'}}>{v>0?`−${fmt(v)}`:'—'}</td>;
                    })}
                    <td style={{textAlign:'right',padding:'6px 10px',fontSize:13,fontWeight:600,color:'var(--red)'}}>−{fmt(totalRefund)}</td>
                  </tr>
                )}

                {/* Expenses total row */}
                <tr style={{background:'rgba(239,68,68,0.07)'}}>
                  <td style={{fontWeight:700,color:'var(--red)'}}>Расходы</td>
                  {months.map(m=><td key={m} style={tdNum(expenseByMonth[m]||0,'var(--red)')}>{fmt(expenseByMonth[m]||0)}</td>)}
                  <td style={tdNum(data.totalExpense,'var(--red)')}>{fmt(data.totalExpense)}</td>
                </tr>

                {/* Expense category rows */}
                {(data.expenseByCategory||[]).map(cat=>{
                  const catTotal = cat.total;
                  return (
                    <tr key={cat.category}>
                      <td style={{paddingLeft:24,fontSize:13,color:'var(--text2)'}}>↳ {SUBTYPE_LABELS[cat.category]||cat.category||'Без категории'}</td>
                      {months.map(m=>{
                        const v = catMonthMap[cat.category]?.[m] || 0;
                        return <td key={m} style={{textAlign:'right',padding:'6px 10px',fontSize:13,color:v>0?'var(--text)':'var(--text3)'}}>{v>0?fmt(v):'—'}</td>;
                      })}
                      <td style={{textAlign:'right',padding:'6px 10px',fontSize:13,fontWeight:600,color:'var(--red)'}}>{fmt(catTotal)}</td>
                    </tr>
                  );
                })}

                {/* Profit row */}
                <tr style={{borderTop:'2px solid var(--border)',background:'rgba(79,142,247,0.07)'}}>
                  <td style={{fontWeight:800,fontSize:15}}>Чистая прибыль</td>
                  {months.map(m=>{
                    const p=(incomeByMonth[m]||0)-(refundByMonth[m]||0)-(expenseByMonth[m]||0);
                    return <td key={m} style={tdNum(p)}>{fmt(p)}</td>;
                  })}
                  <td style={{...tdNum(data.profit),fontWeight:800,fontSize:15}}>{fmt(data.profit)}</td>
                </tr>

                {/* Margin row */}
                <tr>
                  <td style={{fontSize:12,color:'var(--text3)',paddingLeft:8}}>Рентабельность</td>
                  {months.map(m=>{
                    const inc=(incomeByMonth[m]||0)-(refundByMonth[m]||0), exp=expenseByMonth[m]||0;
                    return <td key={m} style={{textAlign:'right',padding:'6px 10px',fontSize:12,color:'var(--text3)'}}>{fmtPct(inc-exp,inc)}</td>;
                  })}
                  <td style={{textAlign:'right',padding:'6px 10px',fontSize:12,color:'var(--text2)',fontWeight:600}}>{fmtPct(data.profit,netIncome)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Дивиденды акционеров — справочный блок, в прибыль не входит */}
        {showDividends && (
          <div className="card" style={{marginBottom:20}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:12,flexWrap:'wrap'}}>
              <div className="section-title" style={{marginBottom:0}}>Дивиденды акционеров</div>
              {canEditAccruals && <button className="btn btn-ghost btn-sm" onClick={()=>setShowAccruals(v=>!v)}>{showAccruals?'Скрыть начисления':'Начисления'}</button>}
            </div>
            <div style={{fontSize:12,color:'var(--text3)',margin:'6px 0 10px'}}>
              По месяцу начисления. В расходы и чистую прибыль не входят. «Начислено» вводится вручную бухгалтером по согласованию с владельцем;
              «Остаток к выплате» = входящий остаток + начислено − выплачено, нарастающим итогом; красное отрицательное — переплата. «Нераспределённая прибыль» — сумма остатков обоих акционеров: вся ли заработанная прибыль выведена. «По доле» — сколько причиталось бы каждому от распределённой прибыли месяца при долях {pct(shares.ak)}/{pct(shares.dominant)}; отклонение показывает, где начисления ушли от пропорции.
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{minWidth:220}}>Акционер</th>
                    {divMonths.map(m=><th key={m} style={thStyle}>{monthHead(m)}</th>)}
                    <th style={thStyle}>ИТОГО</th>
                  </tr>
                </thead>
                <tbody>
                  {SH.map(sh=>{
                    const k = sh.key;
                    const cell = (v, extra={}) => ({ textAlign:'right', padding:'4px 10px', fontSize:12, whiteSpace:'nowrap', ...extra });
                    return (
                      <React.Fragment key={k}>
                        <tr style={{background:'rgba(79,142,247,0.05)'}}>
                          <td style={{fontWeight:700}}>{sh.label} ({pct(shares[k])})</td>
                          {divMonths.map(m=><td key={m}/>)}
                          <td/>
                        </tr>
                        {hasAccruals && (
                          <tr>
                            <td style={{paddingLeft:24,fontSize:13,color:'var(--text2)'}}>Начислено{opening[k]!==0 && <span style={{fontSize:11,color:'var(--text3)'}}> · вх. остаток до периода {fmtSigned(opening[k])}</span>}</td>
                            {divMonths.map(m=>{
                              const v=accMap[k][m]||0, o=openMap[k][m]||0;
                              if (v===0 && o===0) return <td key={m} style={cell(0,{fontSize:13,color:'var(--text3)'})}>—</td>;
                              return <td key={m} style={cell(v,{fontSize:13,color:'var(--text)'})} title={o!==0?'Входящий остаток (история до начала учёта)':''}>
                                {v!==0 ? fmt(v) : null}{o!==0 ? <span style={{fontSize:11,color:'var(--text3)'}}>{v!==0?' + ':''}вх. {fmt(o)}</span> : null}
                              </td>;
                            })}
                            <td style={cell(0,{fontSize:13,fontWeight:600})}>{fmt(accTotal[k])}{openTotal[k]!==0 && <span style={{fontSize:11,color:'var(--text3)',fontWeight:400}}> + вх. {fmt(openTotal[k])}</span>}</td>
                          </tr>
                        )}
                        <tr>
                          <td style={{paddingLeft:24,fontSize:13,color:'var(--text2)'}}>Выплачено</td>
                          {divMonths.map(m=>{ const v=paidMap[k][m]||0; return <td key={m} style={cell(v,{fontSize:13,fontWeight:600,color:v>0?'var(--text)':'var(--text3)'})}>{v>0?fmt(v):'—'}</td>; })}
                          <td style={cell(0,{fontSize:13,fontWeight:700})}>{fmt(paidTotal[k])}</td>
                        </tr>
                        {hasAccruals && (
                          <tr>
                            <td style={{paddingLeft:24,fontSize:13,fontWeight:600}}>Остаток к выплате</td>
                            {divMonths.map((m,i)=>{
                              if (!isActive(m)) return <td key={m} style={cell(0,{color:'var(--text3)'})}>—</td>;
                              const v=running[k][i];
                              return <td key={m} style={cell(v,{fontSize:13,fontWeight:700,color:balColor(v)})} title={v<-0.5?'Переплата: выплачено больше, чем начислено':''}>{fmtSigned(v)}</td>;
                            })}
                            <td style={cell(0,{fontSize:13,fontWeight:800,color:balColor(lastIdx>=0?running[k][lastIdx]:0)})}>{fmtSigned(lastIdx>=0?running[k][lastIdx]:0)}</td>
                          </tr>
                        )}
                        {hasAccruals && (<>
                          <tr>
                            <td style={{paddingLeft:24,fontSize:12,color:'var(--text3)'}}>↳ по доле {pct(shares[k])} от распределённой прибыли</td>
                            {divMonths.map(m=>{ const v=accMonthTotal(m)*shares[k]; return <td key={m} style={cell(v,{color:'var(--text3)'})}>{v!==0?fmt(v):'—'}</td>; })}
                            <td style={cell(0,{color:'var(--text2)'})}>{fmt(accAll*shares[k])}</td>
                          </tr>
                          <tr>
                            <td style={{paddingLeft:24,fontSize:12,color:'var(--text3)'}}>↳ отклонение начисления от доли</td>
                            {divMonths.map(m=>{ if (accMonthTotal(m)===0) return <td key={m} style={cell(0,{color:'var(--text3)'})}>—</td>; const v=(accMap[k][m]||0)-accMonthTotal(m)*shares[k]; return <td key={m} style={cell(v,{fontWeight:600,color:deltaColor(v)})}>{fmtSigned(v)}</td>; })}
                            <td style={cell(0,{fontWeight:700,color:deltaColor(accTotal[k]-accAll*shares[k])})}>{fmtSigned(accTotal[k]-accAll*shares[k])}</td>
                          </tr>
                        </>)}
                      </React.Fragment>
                    );
                  })}
                  <tr style={{borderTop:'2px solid var(--border)',background:'rgba(79,142,247,0.07)'}}>
                    <td style={{fontWeight:700}}>Итого выплачено</td>
                    {divMonths.map(m=>{ const v=paidMonthTotal(m); return <td key={m} style={{textAlign:'right',padding:'6px 10px',fontWeight:600,color:v>0?'var(--text)':'var(--text3)',whiteSpace:'nowrap'}}>{v>0?fmt(v):'—'}</td>; })}
                    <td style={{textAlign:'right',padding:'6px 10px',fontWeight:700,whiteSpace:'nowrap'}}>{fmt(paidAll)}</td>
                  </tr>
                  <tr>
                    <td style={{fontSize:12,color:'var(--text3)',paddingLeft:8}}>Нераспределённая прибыль (остаток обоим акционерам)</td>
                    {divMonths.map((m,i)=>{
                      if (!hasAccruals || !isActive(m)) return <td key={m} style={{textAlign:'right',padding:'6px 10px',fontSize:12,color:'var(--text3)'}}>—</td>;
                      const v = (running.ak[i]||0)+(running.dominant[i]||0);
                      return <td key={m} style={{textAlign:'right',padding:'6px 10px',fontSize:12,fontWeight:600,color:balColor(v),whiteSpace:'nowrap'}}>{fmtSigned(v)}</td>;
                    })}
                    {(()=>{ const v = hasAccruals && lastIdx>=0 ? (running.ak[lastIdx]||0)+(running.dominant[lastIdx]||0) : 0; return <td style={{textAlign:'right',padding:'6px 10px',fontSize:12,fontWeight:700,color:balColor(v),whiteSpace:'nowrap'}}>{hasAccruals?fmtSigned(v):'—'}</td>; })()}
                  </tr>
                </tbody>
              </table>
            </div>

            {canEditAccruals && showAccruals && (
              <div style={{marginTop:16,borderTop:'1px solid var(--border)',paddingTop:12}}>
                <div className="section-title" style={{fontSize:14}}>Начисленные дивиденды — справочник</div>
                <div className="filters" style={{alignItems:'flex-end',marginBottom:10}}>
                  <div className="form-group"><label>Акционер</label>
                    <select value={accForm.shareholder} onChange={e=>setAccForm(f=>({...f,shareholder:e.target.value}))}>
                      <option value="dominant">Доминант</option><option value="ak">АК</option>
                    </select></div>
                  <div className="form-group"><label>Месяц начисления</label>
                    <select value={accForm.month} onChange={e=>setAccForm(f=>({...f,month:e.target.value}))}>
                      {[...new Set([accForm.month, ...MONTH_OPTS])].sort().reverse().map(m=><option key={m} value={m}>{monthLabel(m)}</option>)}
                    </select></div>
                  <div className="form-group"><label>Тип</label>
                    <select value={accForm.kind||'accrual'} onChange={e=>setAccForm(f=>({...f,kind:e.target.value}))}>
                      <option value="accrual">Начисление (доля прибыли)</option><option value="opening">Входящий остаток</option>
                    </select></div>
                  <div className="form-group"><label>Сумма, ₽</label><input type="text" inputMode="decimal" value={accForm.amount} onChange={e=>setAccForm(f=>({...f,amount:e.target.value}))} placeholder="0" style={{width:140}}/></div>
                  <div className="form-group" style={{flex:1,minWidth:220}}><label>Комментарий</label><input type="text" value={accForm.comment} onChange={e=>setAccForm(f=>({...f,comment:e.target.value}))} placeholder="Основание начисления"/></div>
                  <button className="btn btn-primary btn-sm" onClick={saveAccrual} disabled={accSaving}>Добавить</button>
                </div>
                {accErr && <div style={{color:'var(--red)',fontSize:12,marginBottom:8}}>{accErr}</div>}
                <div style={{fontSize:12,color:'var(--text3)',marginBottom:8}}>Отрицательная сумма уменьшает начисление. Несколько строк на один месяц допустимы (доля за месяц, закрытие остатка и т.п.).</div>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Месяц</th><th>Акционер</th><th>Тип</th><th style={{textAlign:'right'}}>Сумма</th><th>Комментарий</th><th>Кто внёс</th><th></th></tr></thead>
                    <tbody>
                      {accruals.length===0 && <tr><td colSpan={7} style={{color:'var(--text3)',fontSize:13}}>За выбранный период начислений нет</td></tr>}
                      {accruals.map(a=>(
                        <tr key={a.id}>
                          <td style={{whiteSpace:'nowrap'}}>{monthLabel(a.month)}</td>
                          <td>{a.shareholder==='ak'?'АК':'Доминант'}</td>
                          <td style={{fontSize:12,color:'var(--text3)'}}>{a.kind==='opening'?'вх. остаток':'начисление'}</td>
                          <td style={{textAlign:'right',fontWeight:600,whiteSpace:'nowrap',color:a.amount<0?'var(--red)':'var(--text)'}}>{fmtSigned(a.amount)}</td>
                          <td style={{fontSize:13,color:'var(--text2)'}}>{a.comment||'—'}</td>
                          <td style={{fontSize:12,color:'var(--text3)'}}>{a.created_by_name||'—'}</td>
                          <td style={{textAlign:'right'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeAccrual(a.id)} title="Удалить">✕</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </>)}
    </div>
  );
}
